import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UploadCloud, FolderPlus, FolderOpen, Loader2, Trash2, ArrowLeft, ImageIcon, Eye, EyeOff, FileText } from "lucide-react";

const isImage = (ct) => (ct || "").startsWith("image/");
const isAudio = (ct) => (ct || "").startsWith("audio/");
const isVideo = (ct) => (ct || "").startsWith("video/");

async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = document.createElement("img");
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const max = 1600;
    let { width, height } = img;
    if (width > max || height > max) {
      const r = Math.min(max / width, max / height);
      width = Math.round(width * r); height = Math.round(height * r);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return file; }
}

export default function Resources() {
  const qc = useQueryClient();
  const thumbRef = useRef(null);
  const fileRef = useRef(null);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [toDeleteSection, setToDeleteSection] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", show: true });
  const [thumb, setThumb] = useState(null);

  const { data: sections = [], isLoading } = useQuery({ queryKey: ["sections"], queryFn: async () => (await api.get("/sections")).data });
  const { data: resources = [] } = useQuery({ queryKey: ["resources"], queryFn: async () => (await api.get("/resources")).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["sections"] }); qc.invalidateQueries({ queryKey: ["resources"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); };

  const createSection = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("show_in_portal", String(form.show));
      if (thumb) fd.append("thumbnail", await compressImage(thumb));
      return (await api.post("/sections", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { refresh(); toast.success("Section created"); setOpenNew(false); setForm({ name: "", description: "", show: true }); setThumb(null); },
    onError: () => toast.error("Could not create section"),
  });

  const toggleShow = useMutation({
    mutationFn: async (s) => { const fd = new FormData(); fd.append("show_in_portal", String(!s.show_in_portal)); return (await api.put(`/sections/${s.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data; },
    onSuccess: () => { refresh(); },
  });

  const delSection = useMutation({
    mutationFn: async (id) => (await api.delete(`/sections/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Section removed"); setToDeleteSection(null); setSelected(null); },
  });

  const uploadFiles = useMutation({
    mutationFn: async ({ files, sectionId }) => {
      const fd = new FormData();
      const processed = await Promise.all(Array.from(files).map(compressImage));
      processed.forEach((f) => fd.append("files", f));
      fd.append("section_id", sectionId);
      return (await api.post("/resources", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { refresh(); toast.success("Files uploaded"); },
    onError: () => toast.error("Upload failed"),
  });

  const delResource = useMutation({
    mutationFn: async (id) => (await api.delete(`/resources/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("File removed"); },
  });

  const filesOf = (sid) => resources.filter((r) => r.section_id === sid);

  // ---- Section detail view ----
  if (selected) {
    const files = filesOf(selected.id);
    return (
      <div data-testid="resources-page">
        <button onClick={() => setSelected(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold-600 hover:text-gold-700"><ArrowLeft className="h-4 w-4" /> All sections</button>
        <PageHeader
          title={selected.name}
          subtitle={selected.description || "Upload the files for this section."}
          action={<Button data-testid="upload-files-button" onClick={() => fileRef.current?.click()} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient"><UploadCloud className="mr-2 h-4 w-4" /> Upload files</Button>}
        />
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="files-input" onChange={(e) => { const picked = e.target.files ? Array.from(e.target.files) : []; e.target.value = ""; if (picked.length) uploadFiles.mutate({ files: picked, sectionId: selected.id }); }} />
        {uploadFiles.isPending && <div className="mb-4 flex items-center gap-2 text-sm text-navy-500"><Loader2 className="h-4 w-4 animate-spin text-gold-500" /> Uploading…</div>}
        {files.length === 0 ? (
          <Card className="flex flex-col items-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-16 text-center shadow-none">
            <UploadCloud className="h-8 w-8 text-gold-500" />
            <p className="mt-3 max-w-sm text-navy-500">No files yet. Upload quotes, images, meditations or guides for this section.</p>
          </Card>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {files.map((r) => {
              const url = `${API}/resources/${r.id}/download`;
              return (
                <Card key={r.id} data-testid={`resource-card-${r.id}`} className="flex flex-col rounded-2xl border-navy-900/5 bg-white p-4 shadow-ambient">
                  <div className="mb-2">
                    {isImage(r.content_type) && <img src={url} alt={r.title} className="h-32 w-full rounded-lg object-cover" />}
                    {isAudio(r.content_type) && <audio controls src={url} className="w-full" />}
                    {isVideo(r.content_type) && <video controls src={url} className="w-full rounded-lg" />}
                    {!isImage(r.content_type) && !isAudio(r.content_type) && !isVideo(r.content_type) && <div className="flex h-32 items-center justify-center rounded-lg bg-sky-50"><FileText className="h-8 w-8 text-sky-400" /></div>}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="truncate text-sm font-medium text-navy-900">{r.title}</div>
                    <Button data-testid={`delete-resource-${r.id}`} variant="outline" size="icon" onClick={() => delResource.mutate(r.id)} className="h-8 w-8 shrink-0 rounded-full border-navy-100 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---- Sections grid ----
  return (
    <div data-testid="resources-page">
      <PageHeader
        title="Resources"
        subtitle="Create sections (folders) with a cover image, then upload files. Choose which appear in the client portal."
        action={<Button data-testid="new-section-button" onClick={() => setOpenNew(true)} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient"><FolderPlus className="mr-2 h-4 w-4" /> New section</Button>}
      />

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-56 animate-pulse rounded-2xl bg-white/60" />)}</div>
      ) : sections.length === 0 ? (
        <Card className="flex flex-col items-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-20 text-center shadow-none">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-50"><FolderOpen className="h-8 w-8 text-gold-500" /></div>
          <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">No sections yet</h3>
          <p className="mt-2 max-w-sm text-navy-500">Create your first section (e.g. "Inspiring Quotes") with a cover image, then upload files into it.</p>
          <Button onClick={() => setOpenNew(true)} className="mt-6 rounded-full bg-gold-500 text-white hover:bg-gold-600"><FolderPlus className="mr-2 h-4 w-4" /> New section</Button>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sections.map((s) => (
            <Card key={s.id} data-testid={`section-card-${s.id}`} className="overflow-hidden rounded-2xl border-navy-900/5 bg-white shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <button onClick={() => setSelected(s)} className="block w-full text-left">
                <div className="relative h-36 overflow-hidden bg-gold-50">
                  {s.thumbnail ? <img src={s.thumbnail} alt={s.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ImageIcon className="h-9 w-9 text-gold-300" /></div>}
                </div>
                <div className="px-5 pt-4">
                  <h3 className="font-serif text-lg font-bold text-navy-900">{s.name}</h3>
                  {s.description && <p className="mt-1 line-clamp-2 text-sm text-navy-500">{s.description}</p>}
                  <div className="mt-2 text-xs text-navy-400">{s.file_count} file{s.file_count === 1 ? "" : "s"}</div>
                </div>
              </button>
              <div className="flex items-center justify-between px-5 py-4">
                <label className="flex items-center gap-2 text-xs font-medium text-navy-600">
                  <Switch data-testid={`section-visible-${s.id}`} checked={!!s.show_in_portal} onCheckedChange={() => toggleShow.mutate(s)} />
                  {s.show_in_portal ? <span className="flex items-center gap-1 text-emerald-600"><Eye className="h-3.5 w-3.5" /> In portal</span> : <span className="flex items-center gap-1 text-navy-400"><EyeOff className="h-3.5 w-3.5" /> Hidden</span>}
                </label>
                <Button data-testid={`delete-section-${s.id}`} variant="outline" size="icon" onClick={() => setToDeleteSection(s)} className="h-8 w-8 rounded-full border-navy-100 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New section dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-900">New Section</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div
              onClick={() => thumbRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-navy-100 bg-ivory py-8 text-center transition-colors hover:border-gold-400"
              data-testid="thumb-dropzone"
            >
              {thumb ? <img src={URL.createObjectURL(thumb)} alt="cover" className="h-28 w-full object-cover" /> : <><ImageIcon className="h-8 w-8 text-gold-500" /><p className="mt-2 text-sm font-medium text-navy-700">Upload a cover image (tile)</p><p className="text-xs text-navy-400">This becomes the section's tile in the portal</p></>}
              <input ref={thumbRef} type="file" accept="image/*" className="hidden" data-testid="thumb-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) setThumb(f); }} />
            </div>
            <div><Label>Section name</Label><Input data-testid="section-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Inspiring Quotes" className="mt-1 focus-visible:ring-gold-500" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 focus-visible:ring-gold-500" /></div>
            <label className="flex items-center gap-3 rounded-xl bg-ivory px-4 py-3">
              <Switch data-testid="section-show-toggle" checked={form.show} onCheckedChange={(v) => setForm({ ...form, show: v })} />
              <span className="text-sm text-navy-700">Show this section in the client portal</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="confirm-section-button" disabled={!form.name || createSection.isPending} onClick={() => createSection.mutate()} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">
              {createSection.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…</> : "Create section"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDeleteSection} onOpenChange={(o) => !o && setToDeleteSection(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Remove section?</AlertDialogTitle><AlertDialogDescription>"{toDeleteSection?.name}" and its files will be removed from the client portal.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-section" onClick={() => delSection.mutate(toDeleteSection.id)} className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
